<script setup lang="ts">
import { computed, ref, watch } from 'vue';
import {
    useQuasar, date, QInput, type QInputProps,
} from 'quasar';
import { isDateValid } from '@/functions/isDateValid';
import type { DateFilter } from '@/types/TableFilters';
import DateInput from '@/components/DateInput.vue';

const props = defineProps<{
    tableLoading: boolean
}>();

const $q = useQuasar();
const emit = defineEmits<{(event: 'apply-filters', filters: DateFilter): void }>();

const startDate = ref<string>('');
const startDateInput = ref<QInput | null>(null);
const endDate = ref<string>('');
const endDateInput = ref<QInput | null>(null);
const filterBy = ref<string>('');
const quickFilter = ref<string>('');
const showFilterError = ref<boolean>(false);
const showDateError = ref<boolean>(false);
const appliedFilters = ref<DateFilter>({ filterBy: '', startDate: '', endDate: '' });
/** Keeps the date filter open if there are validation errors */
const noFilterErrors = ref(true);
const usePopupCalendar = computed(() => $q.screen.xs);

const ruleDateOrEmpty = (val:string) => (!val || isDateValid(val, 'MM/DD/YYYY') || 'Please enter a valid date in the format MM/DD/YYYY');
const dateError = computed(() => {
    if (!startDate.value || !endDate.value) {
        return 'Please enter both a start and end date';
    }

    if (new Date(startDate.value) > new Date(endDate.value)) {
        return 'The start date must be before or equal to the end date';
    }

    return '';
});

const quickFilterEnum = {
    '30 Days': 30,
    '6 months': 180,
    '12 months': 365,
    '24 months': 730,
};
const quickFilterOptions = Object.keys(quickFilterEnum);
const filterByOptions = ['Enrollment Date', 'Graduation Date', 'Exam Date'];

// deselect quick filter if a date is modified after selecting one
watch([startDate, endDate], () => {
    showDateError.value = false;

    if (quickFilter.value) {
        const days = quickFilterEnum[quickFilter.value as keyof typeof quickFilterEnum];
        const calcStart = date.formatDate(new Date(new Date().getTime() - (days * 24 * 60 * 60 * 1000)), 'MM/DD/YYYY');
        const calcEnd = date.formatDate(new Date(), 'MM/DD/YYYY');

        if (startDate.value !== calcStart || endDate.value !== calcEnd) {
            quickFilter.value = '';
        }
    }
});

const selectedQuickFilter = (selection: string) => {
    quickFilter.value = selection;
    const days = quickFilterEnum[selection as keyof typeof quickFilterEnum];

    const newEndDate = new Date();
    endDate.value = date.formatDate(newEndDate, 'MM/DD/YYYY');

    const newStartDate = new Date(newEndDate.getTime() - (days * 24 * 60 * 60 * 1000));
    startDate.value = date.formatDate(newStartDate, 'MM/DD/YYYY');
};

const clearAll = () => {
    filterBy.value = '';
    quickFilter.value = '';
    startDate.value = '';
    endDate.value = '';
    showDateError.value = false;
    showFilterError.value = false;
    appliedFilters.value = { filterBy: '', startDate: '', endDate: '' };

    emit('apply-filters', { filterBy: '', startDate: '', endDate: '' });
};

const dateOptions: Intl.DateTimeFormatOptions = {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
};

const scrollToFilterError = () => {
    document.querySelector('.filter-by')?.scrollIntoView({ behavior: 'smooth' });
};

const validateFilterBy = (): boolean => {
    if (!filterBy.value) {
        showFilterError.value = true;
        scrollToFilterError();
        return false;
    }

    return true;
};

const validateDates = (): boolean => {
    if (dateError.value) {
        showDateError.value = true;
        return false;
    }

    return true;
};

const validateInputs = async (): Promise<boolean> => {
    const isFilterByValid = validateFilterBy();
    const areDatesValid = validateDates();
    const areInputsValid = (
        await startDateInput.value?.validate()
        && await endDateInput.value?.validate()
    ) || false;

    return isFilterByValid && areDatesValid && areInputsValid;
};

const applyFilters = async () => {
    noFilterErrors.value = false;

    if (!await validateInputs()) {
        return;
    }

    appliedFilters.value = { filterBy: filterBy.value, startDate: startDate.value, endDate: endDate.value };
    noFilterErrors.value = true;
    emit('apply-filters', { filterBy: filterBy.value, startDate: startDate.value, endDate: endDate.value });
};

const onCancel = () => {
    filterBy.value = appliedFilters.value.filterBy;
    startDate.value = appliedFilters.value.startDate;
    endDate.value = appliedFilters.value.endDate;
    showDateError.value = false;
    showFilterError.value = false;
};

// emit a clear filter event if the table is loading
watch(() => props.tableLoading, () => {
    if (props.tableLoading) {
        clearAll();
    }
});

const dateInputProps = computed((): Omit<QInputProps, 'modelValue'> => ({
    rules: [ruleDateOrEmpty],
    outlined: true,
    lazyRules: true,
    clearable: true,
    noErrorIcon: true,
}));
</script>
<template>
    <q-btn
        class="date-filter-button q-pa-sm"
        color="fsum-light-blue-2"
        icon="calendar_today"
        dense
        flat
        aria-label="date filter"
        tabindex="0"
        no-caps
    >
        <div
            v-if="appliedFilters.filterBy"
            class="button-label"
        >
            {{ appliedFilters.filterBy }}:
            <span class="no-break">
                {{ new Date(appliedFilters.startDate).toLocaleDateString('en-US', dateOptions) }}
            </span> -
            <span class="no-break">
                {{ new Date(appliedFilters.endDate).toLocaleDateString('en-US', dateOptions) }}
            </span>
        </div>
        <q-menu
            class="date-filter-menu"
            :class="{'covers-button': $q.screen.lt.md || $q.screen.height < $q.screen.sizes.sm }"
            :cover="$q.screen.lt.md || $q.screen.height < $q.screen.sizes.sm"
            @before-hide="onCancel()"
        >
            <div class="q-gutter-x-md q-px-lg q-pb-sm q-pt-xs">
                <h1 class="header q-mb-sm">
                    Date filter
                </h1>
                <div class="row filter-by">
                    <div class="filter-label col-sm-shrink col-12 q-pr-md">
                        <span>Filter by:</span>
                    </div>
                    <q-chip
                        v-for="option in filterByOptions"
                        :key="option"
                        :selected="filterBy === option"
                        class="filter-option q-pa-sm"
                        :color="filterBy === option ? 'badge-blue' : 'white'"
                        :text-color="filterBy === option ? undefined : 'black'"
                        square
                        clickable
                        :icon-right="filterBy === option ? 'close' : undefined"
                        icon-selected="none"
                        :label="option"
                        @click="filterBy === option ? filterBy = '' : filterBy = option; showFilterError = false"
                    />
                </div>
                <div
                    v-if="showFilterError"
                    class="error"
                >
                    Please select one date type to filter by
                </div>
                <div class="row quick-filter">
                    <div class="filter-label col-sm-shrink col-12 q-pr-md">
                        Quick filter:
                    </div>
                    <q-chip
                        v-for="option in quickFilterOptions"
                        :key="option"
                        :selected="quickFilter === option"
                        class="filter-option q-pa-sm"
                        :color="quickFilter === option ? 'badge-blue' : 'white'"
                        :text-color="quickFilter === option ? undefined : 'black'"
                        square
                        clickable
                        :icon-right="quickFilter === option ? 'close' : undefined"
                        icon-selected="none"
                        :label="option"
                        @click="quickFilter === option ? quickFilter = '' : selectedQuickFilter(option)"
                    />
                </div>
            </div>
            <div class="row date justify-between q-gutter-y-md q-px-lg">
                <div
                    class="col-12 col-sm-6 start-date"
                    :class="{'q-pr-lg': $q.screen.gt.xs}"
                >
                    <div class="date-label q-pb-sm">
                        Start <span class="date-format">(MM/DD/YYYY)</span>
                    </div>
                    <DateInput
                        ref="startDateInput"
                        :model-value="startDate"
                        :input-props="dateInputProps"
                        :is-icon-prepended="true"
                        :hide-date-popup="!usePopupCalendar"
                        class="date-input q-pb-none q-mb-sm"
                        :class="!usePopupCalendar ? 'q-pb-md' : 'q-pb-none'"
                        :value="startDate"
                        @update:model-value="startDate = $event"
                    />
                    <q-date
                        v-if="!usePopupCalendar"
                        v-model="startDate"
                        class="external-date-picker"
                        minimal
                        flat
                        mask="MM/DD/YYYY"
                    />
                </div>
                <div class="col-12 col-sm-6 end-date">
                    <div class="date-label q-pb-sm">
                        End <span class="date-format">(MM/DD/YYYY)</span>
                    </div>
                    <DateInput
                        ref="endDateInput"
                        :model-value="endDate"
                        :input-props="dateInputProps"
                        :is-icon-prepended="true"
                        :hide-date-popup="!usePopupCalendar"
                        class="date-input q-pb-none q-mb-sm"
                        :class="!usePopupCalendar ? 'q-pb-md' : 'q-pb-none'"
                        :value="endDate"
                        @update:model-value="endDate = $event"
                    />
                    <q-date
                        v-if="!usePopupCalendar"
                        v-model="endDate"
                        minimal
                        flat
                        class="external-date-picker"
                        mask="MM/DD/YYYY"
                    />
                </div>
            </div>
            <div
                v-if="showDateError"
                class="error q-pt-md q-pl-lg"
            >
                {{ dateError }}
            </div>
            <q-separator
                class="q-mt-md"
                horizontal
                :inset="false"
            />
            <div class="row justify-between button-group q-px-lg q-py-md">
                <div>
                    <q-btn
                        v-close-popup="true"
                        label="Clear All"
                        class="anchor-button q-pr-md"
                        color="fsum-med-blue"
                        :ripple="false"
                        no-caps
                        dense
                        flat
                        @click="clearAll()"
                    />
                </div>
                <div class="q-gutter-x-sm">
                    <q-btn
                        v-close-popup="true"
                        label="Cancel"
                        class="fsum-button-outlined q-px-sm"
                        no-caps
                        dense
                        flat
                        @click="onCancel()"
                    />
                    <q-btn
                        v-close-popup="noFilterErrors"
                        label="Apply"
                        class="fsum-button-dark-blue q-px-sm"
                        no-caps
                        dense
                        flat
                        @click="() => applyFilters()"
                    />
                </div>
            </div>
        </q-menu>
    </q-btn>
</template>
<style lang="scss" scoped>
:deep(.external-date-picker.q-date) {
    width: 270px;
    min-width: 270px;
}
:deep(.q-date__view) {
    padding: 0;
}
@media screen and (min-width: $breakpoint-sm-min) {
    .date-input {
        max-width: 270px;
    }
}

h1.header {
    font-size: 24px;
    font-weight: bold;
    color: var(--fsum-dark-blue2);
    line-height: normal;
}

div.filter-label {
    font-size: 15px;
    font-weight: bold;
    align-content: center;
}

div.date-label {
    font-size: 15px;

    span.date-format {
        color: var(--fsum-med-light-gray-alt);
    }
}

div.error {
    color: var(--fsum-dark-red);
    font-size: 14px;
    font-weight: bold;
}

:deep(.filter-by .q-chip--clickable:focus) {
   box-shadow: none;
}

/** Customize the filter button's outline. I had trouble customizing it with the outline prop */
.date-filter-button  {
    border: 2px solid currentColor;

    @media screen and (min-width: $breakpoint-sm-min) {
        max-height: 40px;
    }
}

/* Change the checkbox color */
:deep(.q-checkbox__bg) {
    border-color: var(--fsum-blue3);
}
:deep(.q-checkbox__inner.q-checkbox__inner--truthy) {
    color: var(--fsum-blue3);
}

/* Completely hide the extra icon on the chips */
:deep(.q-chip__icon--left) {
    display: none;
}

/** Make the icon and text black. Quasar's text-color prop didn't work */
:deep(.q-btn__content i.q-icon) {
    font-size: 20px;
    color: var(--fsum-black);
}
.button-label{
    color: var(--fsum-black);
}
/** q-gutter-x-sm: adds space between button icon and label when they're on the same line */
:deep(.q-btn__content) {
    margin-left: -8px;
}
:deep(.q-btn__content > *) {
    margin-left: 8px;
}
/** Keep dates on one line */
.no-break {
    white-space: nowrap;
}

/** Prevents input errors from overlaying other elements */
:deep(.q-field--error) {
    margin-bottom: 24px;

    @media screen and (max-width: $breakpoint-sm-min) {
        margin-bottom: 56px;
    }
}

:deep(.q-date--portrait-minimal .q-date__view) {
    min-height: initial;
}
:deep(.q-date__calendar-days-container) {
    min-height: initial;
}
</style>
<style lang="scss">
.date-filter-button span.q-btn__content {
    text-align: left;
    justify-content: left;
}
.date-filter-menu.covers-button {
    max-height: calc(100vh - var(--fsum-header-height) - 2rem) !important;
    top: calc(var(--fsum-header-height) + 1rem) !important;
}
</style>
